/**
 * Synths — the individual voices.
 *
 * Every sound in DESCENT is generated by these classes at runtime. There is not
 * one sample in the project, and there is no decodeAudioData call anywhere.
 *
 * ── THE ARCHITECTURAL RULE ──────────────────────────────────────────────────
 * No AudioNode is ever constructed while the game is running. Every voice —
 * including the one-shots — builds its whole node graph once, at unlock, and
 * then lives forever with its output gain sitting at zero. Triggering a sound
 * means scheduling an envelope on an existing AudioParam, which is lock-free,
 * happens on the audio thread, and costs nothing on the main thread. Creating an
 * OscillatorNode per impact is the classic Web Audio mistake: it allocates, it
 * garbage-collects, and the GC pause lands in the middle of the frame that
 * triggered it.
 *
 * ── THE NOISE BANK ──────────────────────────────────────────────────────────
 * Four looping buffers (two white, one pink, one grain) are shared by every
 * voice that needs noise. A single AudioBufferSourceNode can fan out to any
 * number of destinations, so forty noise-based voices cost four sources, not
 * forty. Voices are assigned different sources round-robin so two impacts firing
 * on the same frame are not bit-identical.
 *
 * The buffers are filled from `Rng`, not `Math.random`, so a build's audio is as
 * reproducible as its geometry.
 */

import { Rng } from '../core/RNG';
import { clamp, clamp01 } from '../core/MathX';

// ─────────────────────────────────────────────────────────────────────────────
// Parameter helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A continuous AudioParam driven from the game loop.
 *
 * `setTargetAtTime` is the right primitive for this — it is an exponential
 * approach evaluated on the audio thread, so a filter cutoff tracking speed
 * never zippers even when the frame rate does. But calling it sixty times a
 * second forever piles automation events onto the param's timeline, so this
 * wrapper suppresses calls that would not change anything audible. In practice
 * that cuts the scheduling rate by an order of magnitude.
 */
export class Smooth {
  private p: AudioParam;
  private last = Number.NaN;
  private eps: number;
  private tau: number;

  constructor(p: AudioParam, tau = 0.05, eps = 0.002) {
    this.p = p;
    this.tau = tau;
    this.eps = eps;
  }

  set(v: number, t: number, tau?: number): void {
    if (!isFinite(v)) return;
    const rel = Math.max(Math.abs(v), Math.abs(this.last), 1e-4);
    if (isFinite(this.last) && Math.abs(v - this.last) / rel < this.eps) return;
    this.last = v;
    this.p.setTargetAtTime(v, t, tau ?? this.tau);
  }

  /** Jump immediately — used when a voice is silent so the ramp is inaudible. */
  snap(v: number, t: number): void {
    if (!isFinite(v)) return;
    this.last = v;
    this.p.cancelScheduledValues(t);
    this.p.setValueAtTime(v, t);
  }
}

/** Percussive envelope on a gain param: attack, exponential decay, hard floor. */
export function strike(
  g: AudioParam,
  t: number,
  peak: number,
  attack: number,
  decay: number,
): void {
  const p = Math.max(peak, 1e-4);
  g.cancelScheduledValues(t);
  g.setValueAtTime(Math.max(g.value, 1e-4), t);
  g.linearRampToValueAtTime(p, t + attack);
  // Exponential decay cannot reach zero, so it lands on -80dB and is then
  // clamped flat. Without the final setValueAtTime the tail sits at 1e-4
  // forever and forty voices of that is an audible noise floor.
  g.exponentialRampToValueAtTime(1e-4, t + attack + decay);
  g.setValueAtTime(0, t + attack + decay + 0.001);
}

/** Sustained envelope: attack, hold at peak, release. Used by the horn. */
export function swell(
  g: AudioParam,
  t: number,
  peak: number,
  attack: number,
  hold: number,
  release: number,
): void {
  const p = Math.max(peak, 1e-4);
  g.cancelScheduledValues(t);
  g.setValueAtTime(Math.max(g.value, 1e-4), t);
  g.linearRampToValueAtTime(p, t + attack);
  g.setValueAtTime(p, t + attack + hold);
  g.exponentialRampToValueAtTime(1e-4, t + attack + hold + release);
  g.setValueAtTime(0, t + attack + hold + release + 0.001);
}

/** Downward pitch sweep — the transient in every impact and thunk. */
export function pitchDrop(
  f: AudioParam,
  t: number,
  from: number,
  to: number,
  time: number,
): void {
  f.cancelScheduledValues(t);
  f.setValueAtTime(Math.max(from, 1), t);
  f.exponentialRampToValueAtTime(Math.max(to, 1), t + time);
}

function gain(ctx: BaseAudioContext, v = 0): GainNode {
  const g = ctx.createGain();
  g.gain.value = v;
  return g;
}

function biquad(
  ctx: BaseAudioContext,
  type: BiquadFilterType,
  freq: number,
  q = 1,
  gainDb = 0,
): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  f.gain.value = gainDb;
  return f;
}

// ─────────────────────────────────────────────────────────────────────────────
// Noise bank
// ─────────────────────────────────────────────────────────────────────────────

export type NoiseKind = 'white' | 'pink' | 'grain';

export class NoiseBank {
  private ctx: AudioContext;
  private buffers: AudioBuffer[] = [];
  private sources: AudioBufferSourceNode[] = [];
  private started = false;

  constructor(ctx: AudioContext) {
    this.ctx = ctx;
    const sr = ctx.sampleRate;
    const rng = new Rng('descent-audio-noise');

    this.buffers.push(this.white(sr, 2.0, rng.fork('w0')));
    this.buffers.push(this.white(sr, 2.3, rng.fork('w1')));
    this.buffers.push(this.pink(sr, 2.7, rng.fork('p0')));
    this.buffers.push(this.grain(sr, 3.1, rng.fork('g0')));

    for (const b of this.buffers) {
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.loop = true;
      this.sources.push(s);
    }
  }

  private white(sr: number, secs: number, rng: Rng): AudioBuffer {
    const n = Math.floor(sr * secs);
    const buf = this.ctx.createBuffer(1, n, sr);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = rng.signed();
    return buf;
  }

  /** Paul Kellet's pink filter — cheap, and the spectrum is close enough. */
  private pink(sr: number, secs: number, rng: Rng): AudioBuffer {
    const n = Math.floor(sr * secs);
    const buf = this.ctx.createBuffer(1, n, sr);
    const d = buf.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < n; i++) {
      const w = rng.signed();
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    }
    return buf;
  }

  /**
   * Sparse decaying impulses. This is what makes gravel sound like individual
   * stones rather than like hiss — filtered white noise has no grain structure,
   * and no amount of filtering will give it one.
   */
  private grain(sr: number, secs: number, rng: Rng): AudioBuffer {
    const n = Math.floor(sr * secs);
    const buf = this.ctx.createBuffer(1, n, sr);
    const d = buf.getChannelData(0);
    let i = 0;
    while (i < n) {
      const gap = Math.floor(rng.range(0.0006, 0.008) * sr);
      i += gap;
      if (i >= n) break;
      const amp = rng.range(0.25, 1.0) * (rng.chance(0.5) ? 1 : -1);
      const len = Math.floor(rng.range(0.0008, 0.006) * sr);
      for (let k = 0; k < len && i + k < n; k++) {
        const e = 1 - k / len;
        d[i + k] += amp * e * e * rng.signed();
      }
    }
    return buf;
  }

  start(t: number): void {
    if (this.started) return;
    this.started = true;
    for (let i = 0; i < this.sources.length; i++) {
      // Stagger the loop phase so two voices on different sources never line up.
      this.sources[i].start(t, (i * 0.37) % 1.5);
    }
  }

  /** Tap a source. `index` selects deterministically; kind picks the family. */
  tap(kind: NoiseKind, index = 0): AudioBufferSourceNode {
    if (kind === 'pink') return this.sources[2];
    if (kind === 'grain') return this.sources[3];
    return this.sources[index & 1];
  }

  dispose(): void {
    for (const s of this.sources) {
      try { s.stop(); } catch { /* never started */ }
      s.disconnect();
    }
    this.sources.length = 0;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wind
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wind is four layers, because one filtered noise band tracking speed sounds
 * exactly like one filtered noise band tracking speed.
 *
 *   • body   — pink noise, low-passed. Present from walking pace. Gives weight.
 *   • rush   — white noise through a wide band-pass that opens with speed.
 *   • edge   — high-passed white, only above ~12 m/s. This is the layer that
 *              makes the speed feel dangerous rather than merely fast.
 *   • whistle— a real oscillator, band-passed, barely audible, rising a fifth
 *              across the top of the speed range. A purely noise-based wind has
 *              no pitch, and without pitch the ear cannot tell 20 m/s from 24.
 */
export class WindVoice {
  private bodyGain: GainNode;
  private rushGain: GainNode;
  private edgeGain: GainNode;
  private toneGain: GainNode;

  private bodyF: Smooth;
  private rushF: Smooth;
  private rushQ: Smooth;
  private edgeF: Smooth;
  private toneF: Smooth;

  private gBody: Smooth;
  private gRush: Smooth;
  private gEdge: Smooth;
  private gTone: Smooth;

  private tone: OscillatorNode;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode) {
    const bodyLp = biquad(ctx, 'lowpass', 220, 0.7);
    this.bodyGain = gain(ctx, 0);
    bank.tap('pink').connect(bodyLp).connect(this.bodyGain).connect(out);

    const rushBp = biquad(ctx, 'bandpass', 700, 0.6);
    this.rushGain = gain(ctx, 0);
    bank.tap('white', 0).connect(rushBp).connect(this.rushGain).connect(out);

    const edgeHp = biquad(ctx, 'highpass', 2600, 0.9);
    this.edgeGain = gain(ctx, 0);
    bank.tap('white', 1).connect(edgeHp).connect(this.edgeGain).connect(out);

    this.tone = ctx.createOscillator();
    this.tone.type = 'triangle';
    this.tone.frequency.value = 120;
    const toneBp = biquad(ctx, 'bandpass', 320, 3.5);
    this.toneGain = gain(ctx, 0);
    this.tone.connect(toneBp).connect(this.toneGain).connect(out);

    this.bodyF = new Smooth(bodyLp.frequency, 0.12, 0.01);
    this.rushF = new Smooth(rushBp.frequency, 0.10, 0.01);
    this.rushQ = new Smooth(rushBp.Q, 0.20, 0.02);
    this.edgeF = new Smooth(edgeHp.frequency, 0.14, 0.01);
    this.toneF = new Smooth(this.tone.frequency, 0.30, 0.01);
    this.gBody = new Smooth(this.bodyGain.gain, 0.09, 0.01);
    this.gRush = new Smooth(this.rushGain.gain, 0.09, 0.01);
    this.gEdge = new Smooth(this.edgeGain.gain, 0.12, 0.01);
    this.gTone = new Smooth(this.toneGain.gain, 0.25, 0.02);
  }

  start(t: number): void {
    this.tone.start(t);
  }

  /**
   * @param speed  m/s
   * @param air    0..1 how airborne the rider is — wind opens up off the ground
   * @param duck   0..1 global duck (menus, replay)
   */
  update(t: number, speed: number, air: number, duck: number): void {
    const s = clamp01(speed / 26);
    const s2 = s * s;

    this.bodyF.set(180 + s * 520, t);
    this.gBody.set((0.05 + s2 * 0.26) * duck, t);

    this.rushF.set(500 + s * 1500, t);
    this.rushQ.set(0.75 - s * 0.35, t);
    this.gRush.set((0.02 + s2 * 0.40) * (1 + air * 0.45) * duck, t);

    const edge = clamp01((speed - 11) / 15);
    this.edgeF.set(2200 + edge * 3400, t);
    this.gEdge.set(edge * edge * 0.20 * (1 + air * 0.6) * duck, t);

    const wh = clamp01((speed - 15) / 12);
    this.toneF.set(150 + wh * 130, t);
    this.gTone.set(wh * wh * 0.055 * duck, t);
  }

  silence(t: number): void {
    this.gBody.set(0, t);
    this.gRush.set(0, t);
    this.gEdge.set(0, t);
    this.gTone.set(0, t);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tyres
// ─────────────────────────────────────────────────────────────────────────────

export type SurfaceTone = 'hardpack' | 'gravel' | 'grass' | 'rock' | 'water' | 'snow';

interface TonePreset {
  /** Roll layer: the broad body of the contact patch. */
  rollType: BiquadFilterType;
  rollF: number;
  rollQ: number;
  rollGain: number;
  /** Texture layer: the resonant character that identifies the surface. */
  texSource: NoiseKind;
  texF: number;
  texQ: number;
  texGain: number;
  /** How far the texture filter wanders per second — grit vs. smooth. */
  texWander: number;
  /** Overall level trim so surfaces sit at the same perceived loudness. */
  trim: number;
  /** How much the tone brightens with speed. */
  speedTilt: number;
}

/**
 * Six surfaces that must be identifiable with your eyes shut. The separation is
 * carried by four things at once — filter TYPE, centre frequency, resonance, and
 * which noise family feeds the texture layer — because any one of them alone
 * collapses into "slightly different hiss".
 *
 *   hardpack  smooth low-passed roar, almost no texture. Fast and quiet.
 *   gravel    grain buffer through a high-Q wandering band-pass. Individual stones.
 *   grass     dark, soft, band-limited swish. Quietest of the six.
 *   rock      bright, hard, a fixed high resonance that rings. Aggressive.
 *   water     two overlapping mid bands with heavy wander — sloshing, not hiss.
 *   snow      very dark pink roll with a faint high squeak. Muffled and slow.
 */
const TONES: Record<SurfaceTone, TonePreset> = {
  hardpack: { rollType: 'lowpass', rollF: 900, rollQ: 0.7, rollGain: 1.00, texSource: 'white', texF: 1900, texQ: 3.0, texGain: 0.16, texWander: 0.10, trim: 1.00, speedTilt: 1.0 },
  gravel:   { rollType: 'bandpass', rollF: 1300, rollQ: 0.9, rollGain: 0.72, texSource: 'grain', texF: 2900, texQ: 7.0, texGain: 1.05, texWander: 0.55, trim: 1.12, speedTilt: 1.15 },
  grass:    { rollType: 'lowpass', rollF: 520, rollQ: 0.5, rollGain: 0.78, texSource: 'pink', texF: 880, texQ: 1.8, texGain: 0.34, texWander: 0.22, trim: 0.72, speedTilt: 0.7 },
  rock:     { rollType: 'highpass', rollF: 620, rollQ: 0.8, rollGain: 0.80, texSource: 'white', texF: 2700, texQ: 6.5, texGain: 0.70, texWander: 0.16, trim: 1.05, speedTilt: 1.3 },
  water:    { rollType: 'bandpass', rollF: 760, rollQ: 0.7, rollGain: 0.95, texSource: 'white', texF: 2100, texQ: 1.6, texGain: 0.80, texWander: 0.90, trim: 1.15, speedTilt: 0.9 },
  snow:     { rollType: 'lowpass', rollF: 380, rollQ: 0.45, rollGain: 0.92, texSource: 'pink', texF: 3300, texQ: 9.0, texGain: 0.13, texWander: 0.30, trim: 0.85, speedTilt: 0.5 },
};

export class TyreVoice {
  private rollFilter: BiquadFilterNode;
  private rollGainNode: GainNode;
  private texFilters: Record<NoiseKind, BiquadFilterNode>;
  private texGains: Record<NoiseKind, GainNode>;
  private squealOsc: OscillatorNode;
  private squealGain: GainNode;
  private squealBp: BiquadFilterNode;
  private skidNoiseGain: GainNode;
  private skidBp: BiquadFilterNode;

  private sRollF: Smooth;
  private sRollQ: Smooth;
  private sRollG: Smooth;
  private sTexF: Record<NoiseKind, Smooth>;
  private sTexG: Record<NoiseKind, Smooth>;
  private sSqF: Smooth;
  private sSqG: Smooth;
  private sSkidF: Smooth;
  private sSkidG: Smooth;

  private tone: SurfaceTone = 'hardpack';
  private wander = 0;
  private rng = new Rng('descent-tyre-wander');

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode) {
    this.rollFilter = biquad(ctx, 'lowpass', 900, 0.7);
    this.rollGainNode = gain(ctx, 0);
    bank.tap('white', 0).connect(this.rollFilter).connect(this.rollGainNode).connect(out);

    // One texture chain per noise family, all permanently wired. Changing
    // surface crossfades between them instead of retyping a filter, because
    // switching a BiquadFilterNode's `type` resets its state and clicks.
    this.texFilters = {
      white: biquad(ctx, 'bandpass', 1900, 3),
      pink: biquad(ctx, 'bandpass', 900, 2),
      grain: biquad(ctx, 'bandpass', 2900, 7),
    };
    this.texGains = { white: gain(ctx, 0), pink: gain(ctx, 0), grain: gain(ctx, 0) };
    (['white', 'pink', 'grain'] as NoiseKind[]).forEach((k, i) => {
      bank.tap(k, 1 - i).connect(this.texFilters[k]).connect(this.texGains[k]).connect(out);
    });

    // Lateral-slip squeal: a real pitched partial, not more noise. Tyres howl.
    this.squealOsc = ctx.createOscillator();
    this.squealOsc.type = 'sawtooth';
    this.squealOsc.frequency.value = 900;
    this.squealBp = biquad(ctx, 'bandpass', 1400, 9);
    this.squealGain = gain(ctx, 0);
    this.squealOsc.connect(this.squealBp).connect(this.squealGain).connect(out);

    // Locked-wheel skid: broadband scrape under the squeal.
    this.skidBp = biquad(ctx, 'bandpass', 1800, 1.4);
    this.skidNoiseGain = gain(ctx, 0);
    bank.tap('white', 1).connect(this.skidBp).connect(this.skidNoiseGain).connect(out);

    this.sRollF = new Smooth(this.rollFilter.frequency, 0.08, 0.01);
    this.sRollQ = new Smooth(this.rollFilter.Q, 0.15, 0.02);
    this.sRollG = new Smooth(this.rollGainNode.gain, 0.06, 0.01);
    this.sTexF = {
      white: new Smooth(this.texFilters.white.frequency, 0.06, 0.01),
      pink: new Smooth(this.texFilters.pink.frequency, 0.06, 0.01),
      grain: new Smooth(this.texFilters.grain.frequency, 0.05, 0.01),
    };
    this.sTexG = {
      white: new Smooth(this.texGains.white.gain, 0.07, 0.01),
      pink: new Smooth(this.texGains.pink.gain, 0.07, 0.01),
      grain: new Smooth(this.texGains.grain.gain, 0.07, 0.01),
    };
    this.sSqF = new Smooth(this.squealOsc.frequency, 0.05, 0.01);
    this.sSqG = new Smooth(this.squealGain.gain, 0.04, 0.01);
    this.sSkidF = new Smooth(this.skidBp.frequency, 0.06, 0.01);
    this.sSkidG = new Smooth(this.skidNoiseGain.gain, 0.05, 0.01);
  }

  start(t: number): void {
    this.squealOsc.start(t);
  }

  setSurface(tone: SurfaceTone): void {
    this.tone = tone;
  }

  /**
   * @param speed        contact-patch speed, m/s
   * @param load         0..1 normal force, scales the roll level
   * @param lateralSlip  m/s of sideways scrub
   * @param lockup       0..1 how locked the wheel is (negative slip ratio)
   * @param grounded     0..1
   */
  update(
    t: number,
    dt: number,
    speed: number,
    load: number,
    lateralSlip: number,
    lockup: number,
    grounded: number,
    duck: number,
  ): void {
    const p = TONES[this.tone];
    const s = clamp01(speed / 22);
    const lvl = grounded * clamp01(load) * duck;

    // A slow random walk on the texture filter. This is the difference between
    // a surface that sounds like a loop and one that sounds like ground going
    // past: real grit is not periodic, and a fixed filter over a looping buffer
    // makes the loop point audible within a couple of seconds.
    this.wander += (this.rng.signed() * p.texWander - this.wander * 0.6) * Math.min(dt, 0.05) * 8;
    this.wander = clamp(this.wander, -1, 1);

    const tilt = 1 + s * p.speedTilt * 0.9;
    this.sRollF.set(p.rollF * tilt, t);
    this.sRollQ.set(p.rollQ, t);
    this.sRollG.set(p.rollGain * p.trim * lvl * (0.10 + s * 0.9) * 0.34, t);

    for (const k of ['white', 'pink', 'grain'] as NoiseKind[]) {
      const on = k === p.texSource;
      this.sTexF[k].set(on ? p.texF * tilt * (1 + this.wander * 0.28) : p.texF, t);
      this.sTexG[k].set(on ? p.texGain * p.trim * lvl * (0.06 + s * 0.94) * 0.22 : 0, t);
    }

    // Squeal rises in pitch with scrub speed and dies without load.
    const scrub = clamp01(Math.abs(lateralSlip) / 7);
    this.sSqF.set(620 + scrub * 780 + s * 260, t);
    this.sSqG.set(scrub * scrub * 0.085 * lvl, t);

    const lock = clamp01(lockup);
    this.sSkidF.set(1200 + lock * 1400 + s * 500, t);
    this.sSkidG.set(lock * (0.12 + s * 0.5) * 0.16 * lvl, t);
  }

  silence(t: number): void {
    this.sRollG.set(0, t);
    for (const k of ['white', 'pink', 'grain'] as NoiseKind[]) this.sTexG[k].set(0, t);
    this.sSqG.set(0, t);
    this.sSkidG.set(0, t);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Drivetrain: freewheel + chain
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The freewheel click train — the single detail that makes a bike sound like a
 * bike rather than like a go-kart.
 *
 * A pawl click is an impulse, and there are up to two hundred of them a second
 * at speed. Scheduling two hundred one-shot voices a second from JavaScript is
 * both expensive and, worse, jittery: the click rate would be quantised to the
 * frame rate and you would hear the frame rate.
 *
 * So the train is generated at audio rate instead. A sawtooth oscillator runs at
 * the pawl frequency; a WaveShaper with a steep power curve collapses each ramp
 * into a narrow spike; the spike excites two high-Q band-passes that ring at the
 * pawl's own resonances. Changing the click rate is one `frequency` ramp, the
 * spacing is sample-accurate, and there is no allocation and no scheduling at
 * all. Sweeping the rate glides the clicks exactly the way a coasting wheel does.
 *
 * Pedalling engages the pawls, so the freewheel goes silent and a completely
 * different sound takes over: a lower, broader roller-chain rate through a
 * resonant low band, plus mechanical noise that scales with pedal force. The
 * crossfade between them is the thing that reads as "the rider stopped coasting".
 */
const PAWL_COUNT = 24;
const CHAIN_TEETH = 16;

export class DrivetrainVoice {
  private pawlOsc: OscillatorNode;
  private pawlGain: GainNode;
  private chainOsc: OscillatorNode;
  private chainGain: GainNode;
  private mechGain: GainNode;
  private jitterDepth: GainNode;

  private sPawlF: Smooth;
  private sPawlG: Smooth;
  private sChainF: Smooth;
  private sChainG: Smooth;
  private sMechG: Smooth;
  private sRing: Smooth;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode) {
    // ── Impulse shaper. Shared curve shape, two instances (sharp / soft). ──
    const sharp = ctx.createWaveShaper();
    sharp.curve = powerCurve(1024, 34);
    sharp.oversample = '2x';
    const soft = ctx.createWaveShaper();
    soft.curve = powerCurve(1024, 9);
    soft.oversample = '2x';

    // ── Freewheel ─────────────────────────────────────────────────────────
    this.pawlOsc = ctx.createOscillator();
    this.pawlOsc.type = 'sawtooth';
    this.pawlOsc.frequency.value = 40;

    const ringA = biquad(ctx, 'bandpass', 2600, 14);
    const ringB = biquad(ctx, 'bandpass', 4400, 20);
    const ringMix = gain(ctx, 1);
    this.pawlGain = gain(ctx, 0);

    this.pawlOsc.connect(sharp);
    sharp.connect(ringA).connect(ringMix);
    sharp.connect(ringB).connect(ringMix);
    // A little raw impulse through so the click has a transient, not just a ring.
    const dry = gain(ctx, 0.28);
    sharp.connect(dry).connect(ringMix);

    // Per-click amplitude variation, modulated at audio rate by low-passed
    // noise. Real pawls are not machined to identical tension; a perfectly
    // even click train sounds synthetic within half a second.
    const jitterLp = biquad(ctx, 'lowpass', 26, 0.7);
    this.jitterDepth = gain(ctx, 0.30);
    bank.tap('white', 1).connect(jitterLp).connect(this.jitterDepth);
    const jitterVca = gain(ctx, 0.75);
    this.jitterDepth.connect(jitterVca.gain);

    ringMix.connect(jitterVca).connect(this.pawlGain).connect(out);

    // ── Chain under load ──────────────────────────────────────────────────
    this.chainOsc = ctx.createOscillator();
    this.chainOsc.type = 'sawtooth';
    this.chainOsc.frequency.value = 30;
    const chainLp = biquad(ctx, 'lowpass', 900, 0.8);
    const chainRes = biquad(ctx, 'bandpass', 310, 5.5);
    const chainMix = gain(ctx, 1);
    this.chainGain = gain(ctx, 0);
    this.chainOsc.connect(soft);
    soft.connect(chainLp).connect(chainMix);
    soft.connect(chainRes).connect(chainMix);
    chainMix.connect(this.chainGain).connect(out);

    // Mechanical hash: bottom-bracket and derailleur, rises with pedal force.
    const mechBp = biquad(ctx, 'bandpass', 1150, 2.6);
    this.mechGain = gain(ctx, 0);
    bank.tap('pink').connect(mechBp).connect(this.mechGain).connect(out);

    this.sPawlF = new Smooth(this.pawlOsc.frequency, 0.03, 0.004);
    this.sPawlG = new Smooth(this.pawlGain.gain, 0.05, 0.01);
    this.sChainF = new Smooth(this.chainOsc.frequency, 0.05, 0.005);
    this.sChainG = new Smooth(this.chainGain.gain, 0.06, 0.01);
    this.sMechG = new Smooth(this.mechGain.gain, 0.07, 0.01);
    this.sRing = new Smooth(ringA.frequency, 0.10, 0.01);
  }

  start(t: number): void {
    this.pawlOsc.start(t);
    this.chainOsc.start(t);
  }

  /**
   * @param wheelRate rad/s of the rear wheel
   * @param drive     0..1 how hard the rider is pedalling
   * @param grounded  0..1
   */
  update(t: number, wheelRate: number, drive: number, grounded: number, duck: number): void {
    const rate = Math.abs(wheelRate) / (Math.PI * 2);
    const pawlHz = clamp(rate * PAWL_COUNT, 0.5, 240);
    this.sPawlF.set(pawlHz, t);

    // The freewheel is loudest at a lazy coast and buzzes down as it speeds up —
    // that is genuinely how they behave, and it also stops a 200 Hz click train
    // from turning into a sawtooth drone.
    const rollOff = 1 - clamp01((pawlHz - 90) / 150) * 0.55;
    const spinning = clamp01(rate / 1.2);
    const coast = 1 - clamp01(drive * 1.6);
    this.sPawlG.set(spinning * coast * rollOff * grounded * 0.085 * duck, t);
    this.sRing.set(2200 + clamp01(rate / 12) * 900, t);

    // Chain rate follows the same wheel through the gear, an octave-ish below.
    this.sChainF.set(clamp(rate * CHAIN_TEETH * 0.42, 1, 130), t);
    this.sChainG.set(clamp01(drive) * (0.25 + spinning * 0.75) * 0.10 * duck, t);
    this.sMechG.set(clamp01(drive) * 0.035 * duck, t);
  }

  silence(t: number): void {
    this.sPawlG.set(0, t);
    this.sChainG.set(0, t);
    this.sMechG.set(0, t);
  }
}

/**
 * y = x^p over the waveshaper's -1..1 input, DC-corrected.
 *
 * The curve turns a rising sawtooth ramp into a narrow spike at the top of the
 * ramp: higher `power` means a shorter click. The mean of x^p over [0,1] is
 * 1/(p+1), and it is subtracted here — otherwise every click voice contributes
 * a constant DC offset to the master bus, and a dozen of those eats headroom off
 * the limiter for a signal nobody can hear.
 */
function powerCurve(n: number, power: number): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  const dc = 1 / (power + 1);
  for (let i = 0; i < n; i++) {
    const x = i / (n - 1); // 0..1 across the -1..1 input range
    c[i] = Math.pow(x, power) - dc;
  }
  return c;
}

// ─────────────────────────────────────────────────────────────────────────────
// Suspension
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A damped resonant thunk. Fork and shock are the same voice with different
 * base frequencies; the pitch and level both track compression velocity, so a
 * gentle roll over a root is a soft low knock and a bottom-out on a landing is a
 * hard, higher, louder crack. That mapping is what stops the suspension from
 * sounding like a single stock "thud" sample retriggered.
 */
class ThunkVoice {
  private osc: OscillatorNode;
  private oscGain: GainNode;
  private noiseGain: GainNode;
  private bp: BiquadFilterNode;
  freeAt = 0;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode, idx: number) {
    this.osc = ctx.createOscillator();
    this.osc.type = 'sine';
    this.osc.frequency.value = 140;
    this.oscGain = gain(ctx, 0);
    this.osc.connect(this.oscGain).connect(out);

    this.bp = biquad(ctx, 'bandpass', 240, 2.4);
    this.noiseGain = gain(ctx, 0);
    bank.tap(idx % 2 === 0 ? 'pink' : 'white', idx).connect(this.bp).connect(this.noiseGain).connect(out);
  }

  start(t: number): void {
    this.osc.start(t);
  }

  trigger(t: number, level: number, base: number, bright: number): void {
    const f0 = base * (1 + bright * 0.75);
    pitchDrop(this.osc.frequency, t, f0, f0 * 0.52, 0.10);
    strike(this.oscGain.gain, t, level * 0.55, 0.003, 0.13 + level * 0.06);

    this.bp.frequency.setValueAtTime(180 + bright * 520, t);
    strike(this.noiseGain.gain, t, level * 0.30, 0.002, 0.055);
    this.freeAt = t + 0.22;
  }
}

export class SuspensionPool {
  private voices: ThunkVoice[] = [];
  private next = 0;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode, count = 6) {
    for (let i = 0; i < count; i++) this.voices.push(new ThunkVoice(ctx, bank, out, i));
  }

  start(t: number): void {
    for (const v of this.voices) v.start(t);
  }

  /** @param cv compression velocity, m/s. Sign is ignored; magnitude is the hit. */
  trigger(t: number, cv: number, harshness: number, duck: number): void {
    const level = clamp01(Math.abs(cv) / 3.2);
    if (level < 0.06) return;
    let v = this.voices[this.next];
    for (const c of this.voices) if (c.freeAt < v.freeAt) v = c;
    this.next = (this.next + 1) % this.voices.length;
    v.trigger(t, level * duck, 118 + harshness * 46, level * harshness);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Impacts
// ─────────────────────────────────────────────────────────────────────────────

interface ImpactColor {
  /** Body: where the noise burst sits and how long it rings. */
  bodyF: number;
  bodyQ: number;
  bodyDecay: number;
  bodyType: BiquadFilterType;
  /** Transient: the pitched crack under the noise. */
  toneFrom: number;
  toneTo: number;
  toneDecay: number;
  /** A resonant afterring — rock has one, snow does not. */
  ring: number;
  level: number;
}

const IMPACT_COLORS: Record<SurfaceTone, ImpactColor> = {
  hardpack: { bodyF: 620, bodyQ: 0.9, bodyDecay: 0.20, bodyType: 'lowpass', toneFrom: 190, toneTo: 62, toneDecay: 0.13, ring: 0.15, level: 1.00 },
  gravel:   { bodyF: 1500, bodyQ: 1.1, bodyDecay: 0.26, bodyType: 'bandpass', toneFrom: 210, toneTo: 70, toneDecay: 0.10, ring: 0.30, level: 1.05 },
  grass:    { bodyF: 420, bodyQ: 0.7, bodyDecay: 0.16, bodyType: 'lowpass', toneFrom: 150, toneTo: 55, toneDecay: 0.15, ring: 0.05, level: 0.80 },
  rock:     { bodyF: 2200, bodyQ: 1.6, bodyDecay: 0.30, bodyType: 'bandpass', toneFrom: 300, toneTo: 88, toneDecay: 0.09, ring: 0.85, level: 1.20 },
  water:    { bodyF: 1700, bodyQ: 0.8, bodyDecay: 0.46, bodyType: 'bandpass', toneFrom: 120, toneTo: 240, toneDecay: 0.22, ring: 0.20, level: 1.10 },
  snow:     { bodyF: 300, bodyQ: 0.6, bodyDecay: 0.13, bodyType: 'lowpass', toneFrom: 110, toneTo: 48, toneDecay: 0.10, ring: 0.00, level: 0.72 },
};

class ImpactVoice {
  private bodyFilter: BiquadFilterNode;
  private bodyGain: GainNode;
  private ringFilter: BiquadFilterNode;
  private ringGain: GainNode;
  private tone: OscillatorNode;
  private toneGain: GainNode;
  freeAt = 0;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode, idx: number) {
    this.bodyFilter = biquad(ctx, 'lowpass', 800, 1);
    this.bodyGain = gain(ctx, 0);
    bank.tap(idx % 2 === 0 ? 'white' : 'pink', idx).connect(this.bodyFilter).connect(this.bodyGain).connect(out);

    this.ringFilter = biquad(ctx, 'bandpass', 900, 16);
    this.ringGain = gain(ctx, 0);
    bank.tap('grain').connect(this.ringFilter).connect(this.ringGain).connect(out);

    this.tone = ctx.createOscillator();
    this.tone.type = 'triangle';
    this.tone.frequency.value = 160;
    this.toneGain = gain(ctx, 0);
    this.tone.connect(this.toneGain).connect(out);
  }

  start(t: number): void {
    this.tone.start(t);
  }

  trigger(t: number, severity: number, c: ImpactColor): void {
    const s = clamp01(severity);
    const lvl = (0.16 + s * 0.84) * c.level;

    // Harder hits are brighter AND longer — scaling only the gain gives you a
    // quiet crash and a loud crash that are otherwise the same event.
    this.bodyFilter.type = c.bodyType;
    this.bodyFilter.Q.setValueAtTime(c.bodyQ, t);
    pitchDrop(this.bodyFilter.frequency, t, c.bodyF * (0.7 + s * 1.1), c.bodyF * 0.45, c.bodyDecay * 0.9);
    strike(this.bodyGain.gain, t, lvl * 0.62, 0.002, c.bodyDecay * (0.7 + s * 0.8));

    pitchDrop(this.tone.frequency, t, c.toneFrom * (0.85 + s * 0.4), c.toneTo, c.toneDecay);
    strike(this.toneGain.gain, t, lvl * 0.44, 0.0015, c.toneDecay * (0.8 + s * 0.7));

    if (c.ring > 0.01) {
      this.ringFilter.frequency.setValueAtTime(700 + s * 1400, t);
      strike(this.ringGain.gain, t, lvl * c.ring * 0.24, 0.003, 0.10 + s * 0.16);
    }

    this.freeAt = t + 0.6;
  }
}

export class ImpactPool {
  private voices: ImpactVoice[] = [];
  private next = 0;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode, count = 8) {
    for (let i = 0; i < count; i++) this.voices.push(new ImpactVoice(ctx, bank, out, i));
  }

  start(t: number): void {
    for (const v of this.voices) v.start(t);
  }

  trigger(t: number, severity: number, tone: SurfaceTone, duck: number): void {
    let v = this.voices[this.next];
    for (const c of this.voices) if (c.freeAt < v.freeAt) v = c;
    this.next = (this.next + 1) % this.voices.length;
    const col = IMPACT_COLORS[tone] ?? IMPACT_COLORS.hardpack;
    v.trigger(t, clamp01(severity) * duck, col);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Start horn
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A proper horn, not a beep.
 *
 * Four oscillators — three detuned sawtooths and a square an octave down — into
 * three parallel band-passes tuned to a brass-like formant set (a low body
 * around 520 Hz, the characteristic mid ring at 1180, and the bite at 2400).
 * The envelope has a slow-ish attack with a pitch bloom: the fundamental starts
 * about 1.5% flat and settles over 60ms, which is what an air horn actually
 * does and what stops the note from sounding like a synthesiser patch.
 */
export class HornVoice {
  private oscs: OscillatorNode[] = [];
  private detune = [1.0, 1.006, 0.9935];
  private env: GainNode;
  private sub: OscillatorNode;
  private base = 210;

  constructor(ctx: AudioContext, out: AudioNode) {
    this.env = gain(ctx, 0);

    const mix = gain(ctx, 0.30);
    for (let i = 0; i < 3; i++) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = this.base * this.detune[i];
      o.connect(mix);
      this.oscs.push(o);
    }
    this.sub = ctx.createOscillator();
    this.sub.type = 'square';
    this.sub.frequency.value = this.base * 0.5;
    const subGain = gain(ctx, 0.16);
    this.sub.connect(subGain).connect(mix);

    // Formant bank.
    const sum = gain(ctx, 1);
    const formants: [number, number, number][] = [
      [520, 3.0, 1.00],
      [1180, 5.0, 0.62],
      [2400, 7.5, 0.30],
    ];
    for (const [f, q, g] of formants) {
      const bp = biquad(ctx, 'bandpass', f, q);
      const fg = gain(ctx, g);
      mix.connect(bp).connect(fg).connect(sum);
    }
    // A little direct signal keeps the fundamental from disappearing behind the
    // formants at low volume.
    const direct = gain(ctx, 0.22);
    mix.connect(direct).connect(sum);

    const tame = biquad(ctx, 'lowpass', 3800, 0.7);
    sum.connect(tame).connect(this.env).connect(out);
  }

  start(t: number): void {
    for (const o of this.oscs) o.start(t);
    this.sub.start(t);
  }

  /**
   * @param pitch  multiplier on the base note. The three countdown blips run at
   *               1.0 and the start blast a fourth above, at 1.335.
   * @param long   true for the start gun (a held blast), false for a count blip.
   */
  play(t: number, pitch: number, long: boolean, duck: number): void {
    const f = this.base * clamp(pitch, 0.4, 3.0);
    for (let i = 0; i < 3; i++) {
      const target = f * this.detune[i];
      const p = this.oscs[i].frequency;
      p.cancelScheduledValues(t);
      p.setValueAtTime(target * 0.985, t);
      p.exponentialRampToValueAtTime(target, t + 0.06);
    }
    const sp = this.sub.frequency;
    sp.cancelScheduledValues(t);
    sp.setValueAtTime(f * 0.5 * 0.985, t);
    sp.exponentialRampToValueAtTime(f * 0.5, t + 0.06);

    if (long) swell(this.env.gain, t, 0.42 * duck, 0.030, 0.62, 0.34);
    else swell(this.env.gain, t, 0.30 * duck, 0.018, 0.20, 0.16);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Boost
// ─────────────────────────────────────────────────────────────────────────────

/** A rising filtered whoosh plus a sub sweep. Fires on engage, sustains while on. */
export class BoostVoice {
  private bp: BiquadFilterNode;
  private whoosh: GainNode;
  private sub: OscillatorNode;
  private subGain: GainNode;
  private sustain: GainNode;
  private sustainBp: BiquadFilterNode;
  private sSustain: Smooth;
  private sSustainF: Smooth;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode) {
    this.bp = biquad(ctx, 'bandpass', 500, 1.2);
    this.whoosh = gain(ctx, 0);
    bank.tap('white', 0).connect(this.bp).connect(this.whoosh).connect(out);

    this.sub = ctx.createOscillator();
    this.sub.type = 'sine';
    this.sub.frequency.value = 70;
    this.subGain = gain(ctx, 0);
    this.sub.connect(this.subGain).connect(out);

    this.sustainBp = biquad(ctx, 'bandpass', 1600, 0.8);
    this.sustain = gain(ctx, 0);
    bank.tap('pink').connect(this.sustainBp).connect(this.sustain).connect(out);
    this.sSustain = new Smooth(this.sustain.gain, 0.05, 0.01);
    this.sSustainF = new Smooth(this.sustainBp.frequency, 0.08, 0.01);
  }

  start(t: number): void {
    this.sub.start(t);
  }

  fire(t: number, duck: number): void {
    pitchDrop(this.bp.frequency, t, 340, 4200, 0.34);
    strike(this.whoosh.gain, t, 0.30 * duck, 0.02, 0.44);
    pitchDrop(this.sub.frequency, t, 52, 128, 0.30);
    strike(this.subGain.gain, t, 0.26 * duck, 0.01, 0.36);
  }

  update(t: number, on: boolean, speed: number, duck: number): void {
    this.sSustain.set(on ? 0.12 * duck : 0, t);
    this.sSustainF.set(1200 + clamp01(speed / 26) * 2200, t);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UI
// ─────────────────────────────────────────────────────────────────────────────

export type UiKind = 'tick' | 'confirm' | 'score' | 'checkpoint' | 'warning';

interface UiRecipe {
  /** Note frequencies, played in sequence. */
  notes: number[];
  step: number;
  dur: number;
  type: OscillatorType;
  q: number;
  filter: number;
  level: number;
  /** Noise chiff mixed under the first note. */
  chiff: number;
  /** Detuned second oscillator, in semitone-ish ratio. 1 = unison (off). */
  fifth: number;
}

const UI_RECIPES: Record<UiKind, UiRecipe> = {
  tick:       { notes: [1480], step: 0, dur: 0.035, type: 'square', q: 4, filter: 2600, level: 0.16, chiff: 0.05, fifth: 1 },
  confirm:    { notes: [660, 990], step: 0.075, dur: 0.10, type: 'triangle', q: 3, filter: 2400, level: 0.30, chiff: 0.04, fifth: 1.5 },
  score:      { notes: [880, 1320, 1760], step: 0.052, dur: 0.075, type: 'triangle', q: 5, filter: 3400, level: 0.24, chiff: 0.03, fifth: 2 },
  checkpoint: { notes: [523, 784], step: 0.085, dur: 0.14, type: 'sawtooth', q: 6, filter: 2000, level: 0.26, chiff: 0.16, fifth: 1.5 },
  warning:    { notes: [330, 262, 330], step: 0.13, dur: 0.10, type: 'square', q: 2, filter: 1300, level: 0.30, chiff: 0.02, fifth: 1 },
};

class UiVoice {
  private a: OscillatorNode;
  private b: OscillatorNode;
  private bGain: GainNode;
  private env: GainNode;
  private bp: BiquadFilterNode;
  private chiffGain: GainNode;
  private chiffBp: BiquadFilterNode;
  freeAt = 0;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode, idx: number) {
    this.a = ctx.createOscillator();
    this.a.type = 'triangle';
    this.a.frequency.value = 660;
    this.b = ctx.createOscillator();
    this.b.type = 'triangle';
    this.b.frequency.value = 990;
    this.bGain = gain(ctx, 0);

    const mix = gain(ctx, 1);
    this.a.connect(mix);
    this.b.connect(this.bGain).connect(mix);

    this.bp = biquad(ctx, 'lowpass', 2600, 3);
    this.env = gain(ctx, 0);
    mix.connect(this.bp).connect(this.env).connect(out);

    this.chiffBp = biquad(ctx, 'bandpass', 3200, 2.2);
    this.chiffGain = gain(ctx, 0);
    bank.tap('white', idx).connect(this.chiffBp).connect(this.chiffGain).connect(out);
  }

  start(t: number): void {
    this.a.start(t);
    this.b.start(t);
  }

  play(t: number, r: UiRecipe, duck: number): void {
    this.a.type = r.type;
    this.b.type = r.type;
    this.bGain.gain.setValueAtTime(r.fifth > 1 ? 0.34 : 0, t);
    this.bp.frequency.setValueAtTime(r.filter, t);
    this.bp.Q.setValueAtTime(r.q, t);

    const g = this.env.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(0, t);
    for (let i = 0; i < r.notes.length; i++) {
      const nt = t + i * (r.step || r.dur);
      this.a.frequency.setValueAtTime(r.notes[i], nt);
      this.b.frequency.setValueAtTime(r.notes[i] * r.fifth, nt);
      g.setValueAtTime(r.level * duck, nt);
      g.exponentialRampToValueAtTime(1e-4, nt + r.dur);
    }
    const end = t + Math.max(1, r.notes.length) * (r.step || r.dur) + r.dur;
    g.setValueAtTime(0, end + 0.001);

    if (r.chiff > 0.001) strike(this.chiffGain.gain, t, r.chiff * duck, 0.002, 0.06);
    this.freeAt = end + 0.05;
  }
}

export class UiPool {
  private voices: UiVoice[] = [];
  private next = 0;

  constructor(ctx: AudioContext, bank: NoiseBank, out: AudioNode, count = 4) {
    for (let i = 0; i < count; i++) this.voices.push(new UiVoice(ctx, bank, out, i));
  }

  start(t: number): void {
    for (const v of this.voices) v.start(t);
  }

  play(t: number, kind: UiKind, duck: number): void {
    let v = this.voices[this.next];
    for (const c of this.voices) if (c.freeAt < v.freeAt) v = c;
    this.next = (this.next + 1) % this.voices.length;
    v.play(t, UI_RECIPES[kind] ?? UI_RECIPES.tick, duck);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Master bus
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Master chain: sum → compressor → soft clipper → destination.
 *
 * The compressor is set up as a limiter (high ratio, no knee, fast attack) so a
 * crash landing on gravel with the horn still ringing and three UI blips firing
 * cannot clip. The soft clipper after it is the backstop: a limiter with a 3ms
 * attack still lets the first transient through, and a hard digital clip on a
 * crash is the single ugliest sound a game can make.
 */
export class MasterBus {
  readonly input: GainNode;
  readonly loop: GainNode;
  readonly sfx: GainNode;
  readonly ui: GainNode;
  private master: GainNode;
  private comp: DynamicsCompressorNode;

  constructor(ctx: AudioContext) {
    this.input = gain(ctx, 1);
    this.loop = gain(ctx, 1);
    this.sfx = gain(ctx, 1);
    this.ui = gain(ctx, 1);
    this.loop.connect(this.input);
    this.sfx.connect(this.input);
    this.ui.connect(this.input);

    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = -8;
    this.comp.knee.value = 0;
    this.comp.ratio.value = 18;
    this.comp.attack.value = 0.003;
    this.comp.release.value = 0.14;

    const clipper = ctx.createWaveShaper();
    clipper.curve = softClipCurve(2048, 1.6);
    clipper.oversample = '4x';

    this.master = gain(ctx, 0.9);
    this.input.connect(this.comp).connect(clipper).connect(this.master).connect(ctx.destination);
  }

  setVolume(v: number, t: number): void {
    this.master.gain.setTargetAtTime(clamp(v, 0, 2) * 0.9, t, 0.03);
  }
}

/** tanh-shaped soft clip. Transparent below ~0.7, hard ceiling above 1. */
function softClipCurve(n: number, drive: number): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(x * drive) / Math.tanh(drive);
  }
  return c;
}
